import { type FC, useMemo } from 'react';

import clsx from 'clsx';

import { Flex } from '@/components/common/Flex';
import { Text } from '@/components/common/Text';
import { useAnimatedPercent } from '@/hooks/use-animated-percent';
import { useFormatTransactionStatus } from '@/hooks/use-format-transaction-status';
import { type WithClassName } from '@/types/react';
import { type RegularColor } from '@/types/theme';
import { formatPercent } from '@/utils/number';
import { getColorCssValue, getColorCssValueWithAlpha } from '@/utils/theme';

import { TransactionStatus } from '../../../../../../../../../../../../../generated/graphql';

import styles from './StatusBar.module.scss';

type StatusBarProps = WithClassName & {
  status: TransactionStatus;
  percent: number;
};

const STATUS_COLOR_MAP: Record<TransactionStatus, RegularColor> = {
  [TransactionStatus.Created]: 'yellow',
  [TransactionStatus.Pending]: 'blue',
  [TransactionStatus.Succeed]: 'green',
  [TransactionStatus.Failed]: 'red',
};

export const StatusBar: FC<StatusBarProps> = (props) => {
  const { className, status, percent } = props;

  const formatTransactionStatus = useFormatTransactionStatus();

  const color = STATUS_COLOR_MAP[status];

  const barWidthPercent = useAnimatedPercent(percent);

  const barStyle = useMemo(
    () => ({
      width: `${barWidthPercent}%`,
      background: `linear-gradient(270deg, ${getColorCssValueWithAlpha(color, 0.26)} 0%, ${getColorCssValueWithAlpha(color, 0)} 100%)`,
      borderImageSource: `linear-gradient(270deg, ${getColorCssValue(color)} 0%, ${getColorCssValueWithAlpha(color, 0.26)} 100%)`,
    }),
    [barWidthPercent, color]
  );

  return (
    <Flex className={clsx(styles.root, className)} direction="column" gap={2}>
      <Flex alignItems="center" justifyContent="spaceBetween">
        <Text type="regular14" color="white-32p">
          {formatTransactionStatus(status)}
        </Text>

        <Text type="regular14" color="white-32p">
          {formatPercent(percent / 100)}
        </Text>
      </Flex>

      <div className={styles.barContainer}>
        <div className={styles.bar} style={barStyle} />
      </div>
    </Flex>
  );
};
